import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Icon, IconButton } from "../../ui";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../ui/UIElements/HoverCard";
import SettingsConfig from "./SettingsConfig";

const SettingsCategory = ({ category, isLoading, error }) => {
  const { t } = useTranslation(["settings", "common"]);
  const [isExpanded, setIsExpanded] = useState(true);
  const navigate = useNavigate();
  const contentId = `settings-category-${category.id}`;
  const headingId = `${contentId}-heading`;

  const toggleExpanded = () => setIsExpanded((expanded) => !expanded);

  const getCategoryIcon = (key) => {
    switch (key) {
      case "platform_currency_settings":
        return "DollarSign";
      case "platform_distance_measurement_settings":
        return "MapPin";
      case "platform_date_time_formats":
        return "Clock";
      case "basic_order_types":
        return "ShoppingCart";
      case "pro_preorder":
        return "Package";
      default:
        return "Settings";
    }
  };

  if (error) {
    return (
      <div
        className="border-border bg-card rounded-lg border p-4 shadow-sm"
        role="alert"
      >
        <div className="text-error flex items-center space-x-2">
          <Icon name="AlertCircle" className="h-5 w-5" />
          <span className="text-sm">{t("category.errors.loadFailed")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border-border bg-card overflow-hidden rounded-lg border shadow-sm">
      {/* Category Header */}
      <div className="hover:bg-accent relative flex items-start gap-2 p-4 transition-colors motion-reduce:transition-none">
        <Button
          type="button"
          onClick={toggleExpanded}
          variant="ghost"
          size={null}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          aria-labelledby={headingId}
          className="focus-visible:ring-ring flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 rounded-md text-start whitespace-normal focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="bg-accent text-primary shrink-0 rounded-md p-2">
              <Icon name={getCategoryIcon(category.key)} className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 break-words">
              <h3
                id={headingId}
                className="text-foreground text-lg font-medium"
              >
                {category.name}
              </h3>
              {category.description && (
                <p className="text-muted-foreground mt-1 text-sm">
                  {category.description}
                </p>
              )}
            </div>
          </div>
          <Icon
            name="ChevronDown"
            className={`text-muted-foreground h-5 w-5 shrink-0 transition-transform duration-200 motion-reduce:transition-none ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </Button>
        <div className="shrink-0">
          <HoverCard>
            <HoverCardTrigger asChild>
              <IconButton
                type="button"
                onClick={() => navigate("/support")}
                emphasis="ghost"
                intent="neutral"
                size="iconTouch"
                aria-label={t("category.actions.helpTitle")}
              >
                <Icon name="HelpCircle" className="h-4 w-4" />
              </IconButton>
            </HoverCardTrigger>
            <HoverCardContent side="left" className="max-w-xs">
              <div className="text-center">
                <p className="mb-2 text-sm">{t("category.helpPrompt")}</p>
                <Button
                  type="button"
                  onClick={() => navigate("/support")}
                  emphasis="solid"
                  intent="brand"
                  size="sm"
                >
                  {t("category.actions.goToSupport")}
                </Button>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>
      </div>

      {/* Category Content */}
      {isExpanded && (
        <div id={contentId} className="border-border border-t">
          {isLoading ? (
            <div className="space-y-4 p-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="border-border bg-background rounded-lg border p-4"
                >
                  <div className="mb-3 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-1 flex items-center space-x-2">
                        <div className="bg-muted h-4 w-32 animate-pulse rounded motion-reduce:animate-none"></div>
                      </div>
                      <div className="bg-muted mb-2 h-3 w-48 animate-pulse rounded motion-reduce:animate-none"></div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="bg-muted h-6 w-16 animate-pulse rounded motion-reduce:animate-none"></div>
                      <div className="bg-muted h-6 w-12 animate-pulse rounded motion-reduce:animate-none"></div>
                    </div>
                  </div>
                  <div className="bg-muted h-9 animate-pulse rounded motion-reduce:animate-none"></div>
                </div>
              ))}
            </div>
          ) : category?.configs && category?.configs.length > 0 ? (
            <div className="space-y-4 p-4">
              {category?.configs.map((config) => (
                <SettingsConfig key={config.id} config={config} />
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground p-6 text-center">
              <Icon
                name="Settings"
                className="mx-auto mb-2 h-8 w-8 opacity-50"
              />
              <p className="text-sm">{t("category.empty")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SettingsCategory;
